import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Building2, User, CreditCard, Briefcase, Stethoscope } from "lucide-react";
import { ADMIN_TENANTS_URL, ADMIN_PLANS_URL } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";

const TRUSTEDGE_FEATURES = [
    { key: 'dashboard',              label: 'Dashboard' },
    { key: 'customers',              label: 'Customers' },
    { key: 'followup',               label: 'Follow Up' },
    { key: 'tasks',                  label: 'Tasks' },
    { key: 'report_timeline',        label: 'Report – Timeline' },
    { key: 'report_lead_sources',    label: 'Report – Lead Meta Form' },
    { key: 'report_lead_types',      label: 'Report – Lead Source' },
    { key: 'report_lead_potential',  label: 'Report – Lead Potential' },
    { key: 'report_sales_forecast',  label: 'Report – Sales Forecast' },
    { key: 'report_engagement',      label: 'Report – Engagement' },
    { key: 'report_conversion',      label: 'Report – Conversion' },
    { key: 'report_email_analytics', label: 'Report – Email Analytics' },
    { key: 'whatsapp_campaigns',     label: 'WhatsApp – Campaigns' },
    { key: 'whatsapp_inbox',         label: 'WhatsApp – Inbox' },
    { key: 'whatsapp_integration',   label: 'WhatsApp – Integration' },
    { key: 'whatsapp_templates',     label: 'WhatsApp – Templates' },
    { key: 'email_integration',      label: 'Email – Integration' },
    { key: 'email_templates',        label: 'Email – Templates' },
];

const ENGAGE_FEATURES = [
    { key: 'engage_dashboard',           label: 'Dashboard' },
    { key: 'engage_enquiries',           label: 'Patient Enquiries' },
    { key: 'engage_referring_doctors',   label: 'Referring Doctors' },
    { key: 'engage_ambulance',           label: 'Ambulance Partners' },
    { key: 'engage_corporate_accounts',  label: 'Corporate Accounts' },
    { key: 'engage_health_camps',        label: 'Health Camps' },
    { key: 'engage_digital_marketing',   label: 'Digital Marketing' },
    { key: 'engage_followups',           label: 'Follow-ups' },
    { key: 'engage_bulk_import',         label: 'Bulk Import' },
    { key: 'engage_reports',             label: 'Reports' },
    { key: 'engage_ai_caller',           label: 'AI Caller' },
    { key: 'engage_partner_portal',      label: 'Partner Portal' },
    { key: 'whatsapp_campaigns',         label: 'WhatsApp – Campaigns' },
    { key: 'whatsapp_inbox',             label: 'WhatsApp – Inbox' },
    { key: 'whatsapp_integration',       label: 'WhatsApp – Integration' },
    { key: 'email_integration',          label: 'Email – Integration' },
];

const FEATURES_BY_PRODUCT = {
    trustedge: TRUSTEDGE_FEATURES,
    engage:    ENGAGE_FEATURES,
};

const STEPS = [
    { label: "Organization", icon: Building2 },
    { label: "Admin User",   icon: User },
    { label: "Plan",         icon: CreditCard },
];

const Field = ({ label, name, value, onChange, type = "text", placeholder, required }) => (
    <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
            type={type} name={name} value={value} onChange={onChange} required={required}
            placeholder={placeholder}
            className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
        />
    </div>
);

const CreateClient = () => {
    const navigate      = useNavigate();
    const { fetch, create } = useApiMethods();
    const [step, setStep]   = useState(0);
    const [plans, setPlans] = useState([]);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        product_type: "trustedge",
        org_name: "", notes: "",
        admin_fname: "", admin_lname: "", admin_email: "", admin_username: "", admin_password: "",
        plan_id: "", billing_start_date: "", billing_renewal_date: "",
    });

    const PRODUCTS = [
        { value: "trustedge", label: "Gro Wing CRM",   description: "Standard sales CRM (leads, quotations, onboarding)", icon: Briefcase },
        { value: "engage",    label: "Hospital Engage", description: "Hospital CRM (enquiries, referring doctors, cadences)", icon: Stethoscope },
    ];

    useEffect(() => {
        fetch(ADMIN_PLANS_URL, { product_type: form.product_type }, null, true).then(res => {
            if (res?.status === "success") setPlans(res.data || []);
        });
    }, [form.product_type]);

    const activeFeatures = FEATURES_BY_PRODUCT[form.product_type] || TRUSTEDGE_FEATURES;

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "product_type") {
            setForm(f => ({ ...f, product_type: value, plan_id: "" }));
        } else {
            setForm(f => ({ ...f, [name]: value }));
        }
    };

    const handleNext = () => {
        if (step === 0 && !form.org_name.trim()) { ToastMsg("error", "Organization name required"); return; }
        if (step === 1 && (!form.admin_fname || !form.admin_email || !form.admin_username || !form.admin_password)) {
            ToastMsg("error", "All admin fields are required"); return;
        }
        setStep(s => s + 1);
    };

    const handleSubmit = async () => {
        setSaving(true);
        const res = await create(ADMIN_TENANTS_URL, form, false, setSaving, true);
        if (res?.status === "success") {
            ToastMsg("success", res.message || "Client created successfully");
            navigate("/admin/dashboard");
        } else {
            ToastMsg("error", res?.message || "Failed to create client");
        }
        setSaving(false);
    };

    const selectedPlan = plans.find(p => String(p.id) === String(form.plan_id));

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
            <div className="max-w-2xl mx-auto">
                {/* Back */}
                <button onClick={() => navigate("/admin/dashboard")}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 mb-6">
                    <ArrowLeft className="w-4 h-4" /> Back to Dashboard
                </button>

                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {/* Step indicator */}
                    <div className="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-4">New Client</h1>
                        <div className="flex items-center gap-2">
                            {STEPS.map((s, i) => {
                                const Icon = s.icon;
                                return (
                                    <div key={i} className="flex items-center gap-2">
                                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${i === step ? "bg-indigo-600 text-white" : i < step ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" : "bg-gray-100 dark:bg-gray-700 text-gray-500"}`}>
                                            {i < step ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                                            {s.label}
                                        </div>
                                        {i < STEPS.length - 1 && <div className="w-6 h-px bg-gray-200 dark:bg-gray-700" />}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="p-6 space-y-4">
                        {/* Step 0: Organization */}
                        {step === 0 && (
                            <>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Product <span className="text-red-500">*</span>
                                    </label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {PRODUCTS.map(p => {
                                            const Icon = p.icon;
                                            const selected = form.product_type === p.value;
                                            return (
                                                <label key={p.value}
                                                    className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${selected ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : "border-gray-200 dark:border-gray-700 hover:border-indigo-300"}`}>
                                                    <input type="radio" name="product_type" value={p.value} checked={selected} onChange={handleChange} className="mt-1" />
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <Icon className="w-4 h-4 text-indigo-600" />
                                                            <span className="font-medium text-sm text-gray-900 dark:text-white">{p.label}</span>
                                                        </div>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{p.description}</p>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                                <Field label="Organization Name" name="org_name" value={form.org_name} onChange={handleChange} placeholder="e.g. Apollo Hospitals" required />
                                <div className="grid grid-cols-2 gap-4">
                                    <Field label="Billing Start Date" name="billing_start_date" value={form.billing_start_date} onChange={handleChange} type="date" />
                                    <Field label="Billing Renewal Date" name="billing_renewal_date" value={form.billing_renewal_date} onChange={handleChange} type="date" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                                    <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Internal notes about this client…"
                                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500/30" />
                                </div>
                            </>
                        )}

                        {/* Step 1: Admin User */}
                        {step === 1 && (
                            <>
                                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs text-blue-700 dark:text-blue-300">
                                    This user will be created as <strong>super_admin</strong> in the client's database.
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <Field label="First Name" name="admin_fname" value={form.admin_fname} onChange={handleChange} placeholder="John" required />
                                    <Field label="Last Name" name="admin_lname" value={form.admin_lname} onChange={handleChange} placeholder="Doe" />
                                </div>
                                <Field label="Email" name="admin_email" value={form.admin_email} onChange={handleChange} type="email" placeholder="admin@client.com" required />
                                <Field label="Username" name="admin_username" value={form.admin_username} onChange={handleChange} placeholder="admin_login" required />
                                <Field label="Password" name="admin_password" value={form.admin_password} onChange={handleChange} type="password" placeholder="Minimum 6 characters" required />
                            </>
                        )}

                        {/* Step 2: Plan */}
                        {step === 2 && (
                            <>
                                <div className="space-y-3">
                                    {plans.map(p => (
                                        <label key={p.id} className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${String(form.plan_id) === String(p.id) ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : "border-gray-200 dark:border-gray-700 hover:border-indigo-300"}`}>
                                            <input type="radio" name="plan_id" value={p.id} checked={String(form.plan_id) === String(p.id)} onChange={handleChange} className="mt-1" />
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between">
                                                    <span className="font-medium text-gray-900 dark:text-white">{p.name}</span>
                                                    <span className="text-sm font-semibold text-indigo-600">₹{p.price_monthly}/mo</span>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-0.5">{p.description || `Up to ${p.max_users} users`}</p>
                                                <div className="mt-2 flex flex-wrap gap-1">
                                                    {Object.entries(p.features).filter(([, v]) => v).slice(0, 6).map(([k]) => (
                                                        <span key={k} className="px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                                                            {activeFeatures.find(f => f.key === k)?.label || k}
                                                        </span>
                                                    ))}
                                                    {Object.entries(p.features).filter(([, v]) => v).length > 6 && (
                                                        <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">+{Object.entries(p.features).filter(([, v]) => v).length - 6} more</span>
                                                    )}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                    {plans.length === 0 && (
                                        <p className="text-sm text-gray-400 text-center py-4">No plans yet — <button className="text-indigo-600 underline" onClick={() => navigate("/admin/plans")}>create a plan first</button></p>
                                    )}
                                </div>
                            </>
                        )}

                    </div>

                    {/* Footer */}
                    <div className="px-6 pb-6 flex justify-between">
                        <button onClick={() => step === 0 ? navigate("/admin/dashboard") : setStep(s => s - 1)}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                            <ArrowLeft className="w-4 h-4" /> {step === 0 ? "Cancel" : "Back"}
                        </button>
                        {step < STEPS.length - 1 ? (
                            <button onClick={handleNext}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
                                Next <ArrowRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <button onClick={handleSubmit} disabled={saving}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg">
                                {saving ? "Creating…" : <><Check className="w-4 h-4" /> Create Client</>}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateClient;
